import { useEffect, useMemo, useState } from "react";
import { getContractItemSections } from "../../services/contractItemStructure.service";
import { ALL_PAYMENT_SCOPES } from "./paymentScopes";
const EMPTY_SCOPES = [];

// Keep real item IDs and their hierarchy; never build aggregate payment items.
export function filterPaymentItemsByScope(items, scopeId) {
  return items.filter((item) => item.contractItemScopeId === scopeId).map((item) => ({
    ...item,
    children: item.children ? filterPaymentItemsByScope(item.children, scopeId) : item.children,
  }));
}

export default function usePaymentItemScopes({ open, contractId, items, preferredScopeId, includeAll = false, payableOnly = false }) {
  const [structure, setStructure] = useState({ sections: [], loading: true, contractId: null });
  const [sectionId, setSectionId] = useState(null);
  const [scopeId, setScopeId] = useState(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setStructure({ sections: [], loading: true, contractId });
    setSectionId(includeAll && preferredScopeId == null ? ALL_PAYMENT_SCOPES : null);
    setScopeId(preferredScopeId ?? null);
    getContractItemSections(contractId).then((loadedSections) => {
      if (!active) return;
      const sections = payableOnly ? loadedSections.map((section) => ({ ...section,
        scopes: (section.scopes || []).filter((scope) => scope.scopeType !== "TOTAL" && scope.isEditable !== false),
      })) : loadedSections;
      setSectionId(sections.find((section) => section.scopes?.some(
        (scope) => scope.contractItemScopeId === preferredScopeId
      ))?.contractSectionId ?? (includeAll ? ALL_PAYMENT_SCOPES : null));
      setStructure({ sections, loading: false, contractId });
    }).catch(() => {
      if (active) setStructure({ sections: [], loading: false, contractId,
        error: "Không thể tải phạm vi hạng mục. Vui lòng thử lại." });
    });
    return () => { active = false; };
  }, [open, contractId, preferredScopeId, includeAll, payableOnly, revision]);

  const isAll = includeAll && sectionId === ALL_PAYMENT_SCOPES;
  const section = isAll ? undefined : structure.sections.find((entry) => entry.contractSectionId === sectionId)
    || structure.sections[0];
  const scopes = section?.scopes || EMPTY_SCOPES;
  const scope = scopes.find((entry) => entry.contractItemScopeId === scopeId) || scopes[0];
  const loading = structure.loading || structure.contractId !== contractId;
  const isTotal = scope?.scopeType === "TOTAL";
  const scopeIds = useMemo(() => {
    if (isAll || !structure.sections.length) return null;
    if (!scope) return [];
    return isTotal ? scopes.filter((entry) => entry.scopeType === "COMMUNE").map((entry) => entry.contractItemScopeId)
      : [scope.contractItemScopeId];
  }, [isAll, structure.sections.length, scope, scopes, isTotal]);
  const viewItems = useMemo(() => {
    if (loading || structure.error) return [];
    if (scopeIds == null) return items;
    return scopeIds.flatMap((id) => filterPaymentItemsByScope(items, id));
  }, [items, loading, structure.error, scopeIds]);
  const filteredItems = useMemo(() => {
    if (loading || structure.error || isTotal) return [];
    if (!structure.sections.length) return items;
    return scope ? filterPaymentItemsByScope(items, scope.contractItemScopeId) : [];
  }, [items, loading, structure.error, structure.sections.length, scope, isTotal]);

  return { sections: structure.sections, section, scopes, scope, items: filteredItems,
    viewItems, scopeIds, isAll, readOnly: isTotal || section?.isActive === false || scope?.isEditable === false,
    loading, error: structure.error, isTotal,
    changeSection: (id) => { setSectionId(id); setScopeId(null); },
    changeScope: setScopeId, retry: () => setRevision((value) => value + 1) };
}
